import { useEffect, useState } from "react";

// The dashboard-wide gross/net switch.
//
// Every revenue figure this tool has ever shown is GROSS — subscription plus
// overflow plus ticketing and promoter fees, with no account taken of Stripe's
// own processing cut. Since Sep 2026 the rollup records that cut per revenue
// stream, so the same figures can be shown net of it. This module holds which
// of the two the reader has asked for.
//
// ## Why this is one global setting and not a control on each card
//
// Gross and net are not two views of one card, they are two ways of reading
// the whole page. A dashboard showing Estimated MRR net beside a revenue chart
// still gross is worse than one showing either consistently — a reader
// comparing two tiles has no reason to suspect they are on different bases,
// which is the same failure the required `freshness` prop exists to prevent
// one axis over. So the control is in the sidebar, which outlives every
// screen, and there is exactly one of it.
//
// ## Why a module store rather than context or a URL param
//
// Both alternatives were considered against what this folder actually does:
//
//  - **A URL param, like `?range=`.** That is the established pattern here and
//    it is the right one for a per-screen filter, which is what every existing
//    use of it is. It is the wrong one for a setting that must survive
//    navigation: search params are per-route, so walking from Home to a
//    customer detail page would silently drop back to gross. Rewriting the
//    param on every navigation to compensate means teaching every link in the
//    tool about this setting.
//  - **React context.** Would work, and would mean a provider in
//    `MasterDashboardScreen` plus a `useContext` in every consumer. This
//    folder has no context anywhere today, and a provider is the heavier of
//    the two answers for a single enum that never changes shape.
//
// A module-level store read through `useSyncExternalStore` (React 18, already
// this repo's version) needs no provider, works identically from the sidebar
// and from any screen at any depth, and is one small file with no new
// dependency — which is the same reasoning `dashboardApi.ts` and `volume.ts`
// were written under.
//
// ## Why sessionStorage and not localStorage
//
// **Gross must be what a new visit shows**, because gross is what every number
// on this dashboard has always meant and what any figure quoted from it so far
// refers to. A setting that persisted across days would eventually have
// someone open the tool, read a figure, and quote it without noticing it was
// net — the same "plausible-looking number that is quietly a different
// measurement" this folder keeps guarding against. Session scope keeps the
// switch sticky while someone is working and resets it for the next visit.

export type RevenueBasis = "gross" | "net";

export const DEFAULT_REVENUE_BASIS: RevenueBasis = "gross";

const STORAGE_KEY = "markit.masterDashboard.revenueBasis";

const isBasis = (value: unknown): value is RevenueBasis =>
  value === "gross" || value === "net";

/**
 * Storage access is wrapped because it throws outright in some contexts —
 * Safari's private mode, and any browser configured to block site data. A
 * dashboard that fails to render because it could not remember a toggle would
 * be a strictly worse trade than forgetting the toggle.
 */
const readStored = (): RevenueBasis => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return isBasis(raw) ? raw : DEFAULT_REVENUE_BASIS;
  } catch {
    return DEFAULT_REVENUE_BASIS;
  }
};

let current: RevenueBasis = readStored();
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getRevenueBasis = (): RevenueBasis => current;

export const setRevenueBasis = (next: RevenueBasis) => {
  if (next === current) return;
  current = next;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Same reasoning as `readStored` — the toggle still works for this page
    // view, it just will not survive a reload.
  }
  listeners.forEach(listener => listener());
};

/**
 * The basis every revenue-showing component reads.
 *
 * **Subscribe-in-an-effect rather than `useSyncExternalStore`, and that is a
 * types constraint rather than a preference.** The runtime here is React
 * 18.3.1, which has that hook — but this repo pins `@types/react` at 17, where
 * it does not exist, so using it fails the type check. Bumping the React types
 * repo-wide to satisfy one file in an internal tool is exactly the kind of
 * blast radius this folder's isolation rule exists to avoid, so the hook is
 * written against the types the repo actually has.
 *
 * The mount re-read is not redundant: the value can change between the render
 * that seeded `useState` and the effect that subscribes — the sidebar toggle
 * and a screen mounting are ordinary neighbours here — and without it that
 * component would hold a stale basis until the next change.
 */
export const useRevenueBasis = (): RevenueBasis => {
  const [basis, setBasis] = useState<RevenueBasis>(current);
  useEffect(() => {
    setBasis(current);
    return subscribe(() => setBasis(getRevenueBasis()));
  }, []);
  return basis;
};

/** True when the reader has asked for figures net of Stripe's cut. */
export const useIsNetBasis = (): boolean => useRevenueBasis() === "net";

/**
 * One gross figure and its Stripe fee, resolved to what should be displayed.
 *
 * Returns `null` when the net basis is asked for and the fee is not known —
 * which is emphatically not the same as a zero fee. Every figure on this
 * dashboard from before Sep 2026 has no fee recorded against it, and showing
 * gross under a net label would assert Markit paid Stripe nothing. The callers
 * render `null` as "—" with a reason, the same rule the rest of this tool runs
 * on for a number that cannot be known.
 */
export const applyBasis = (
  grossCents: number,
  feeCents: number | null | undefined,
  basis: RevenueBasis
): number | null => {
  if (basis === "gross") return grossCents;
  if (feeCents === null || feeCents === undefined) return null;
  return grossCents - feeCents;
};

/** The word for the current basis, for labels and notes. */
export const basisLabel = (basis: RevenueBasis): string =>
  basis === "net" ? "net of Stripe fees" : "gross";
